import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { MaintenanceModel } from '../../enums/maintenance-model.enum';
import { UserAssetLookupItem } from '../../models/user-asset-lookup.model';
import { LookupService } from '../../services/lookup.service';
import { TranslocoDirective } from '@jsverse/transloco';

export interface AssetDropdownItem extends UserAssetLookupItem {}

@Component({
  selector: 'app-asset-search-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoDirective],
  templateUrl: './asset-search-dropdown.component.html',
  styleUrl: './asset-search-dropdown.component.scss'
})
export class AssetSearchDropdownComponent implements OnChanges, OnDestroy {
  private readonly PAGE_SIZE = 50;
  private lookupService = inject(LookupService);
  private errorHandlerService = inject(ErrorHandlerService);
  private cancelSubject: Subject<void> = new Subject<void>();
  private searchSubject: Subject<string> = new Subject<string>();
  private searchSubscription: Subscription;

  @Input() accountIds: number[] = [];
  @Input() selectedAssetId: number | null = null;
  @Input() selectedAssetName: string = '';
  @Input() showRequiredError: boolean = false;
  @Input() dropdownMaxHeight: string = '250px';
  @Output() assetSelected = new EventEmitter<AssetDropdownItem>();

  assets: AssetDropdownItem[] = [];
  searchTerm: string = '';
  isOpen: boolean = false;
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  page: number = 1;
  totalPages: number = 0;

  constructor() {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((term) => {
        this.searchTerm = term;
        this.resetAndLoadAssets();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accountIds'] && !changes['accountIds'].firstChange) {
      this.resetAndLoadAssets();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    this.searchSubscription.unsubscribe();
  }

  get selectedLabel(): string {
    return this.selectedAssetName || this.assets.find(a => a.assetId === this.selectedAssetId)?.assetName || 'Select asset';
  }

  get hasMorePages(): boolean {
    return this.totalPages === 0 || this.page < this.totalPages;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.close();
  }

  toggle(): void {
    this.isOpen = !this.isOpen;

    if (this.isOpen && this.assets.length === 0) {
      this.resetAndLoadAssets();
    }
  }

  close(): void {
    this.isOpen = false;
  }

  onSearchTermChange(value: string): void {
    this.searchSubject.next(value);
  }

  onMenuScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom <= 48 && this.hasMorePages && !this.isLoading && !this.isLoadingMore) {
      this.loadAssets(this.page + 1);
    }
  }

  selectAsset(asset: AssetDropdownItem): void {
    this.assetSelected.emit(asset);
    this.searchTerm = '';
    this.isOpen = false;
  }

  private resetAndLoadAssets(): void {
    this.page = 1;
    this.totalPages = 0;
    this.assets = [];
    this.loadAssets(1);
  }

  private async loadAssets(page: number): Promise<void> {
    if (page === 1) {
      this.isLoading = true;
      this.cancelSubject.next();
    } else {
      this.isLoadingMore = true;
    }

    try {
      const response = await this.lookupService.getUserAssetsAsync({
        accountIds: this.accountIds ?? [],
        page,
        pageSize: this.PAGE_SIZE,
        searchTerm: this.searchTerm.trim() || undefined
      }, this.cancelSubject);

      const results = (response.data?.results ?? []).map(asset => ({
        accountId: asset.accountId,
        assetId: asset.assetId,
        assetName: asset.assetName,
        maintenanceModel: asset.maintenanceModel ?? MaintenanceModel.InHouse
      }));

      this.assets = page === 1 ? results : [...this.assets, ...results];
      this.page = page;
      this.totalPages = response.data?.totalPages ?? 0;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          if (page === 1) {
            this.assets = [];
          }
        }
      });
    } finally {
      this.isLoading = false;
      this.isLoadingMore = false;
    }
  }
}
